import { Icon, Tooltip } from "@shopify/polaris"
import {
	AddProductMajor,
	CategoriesMajor,
	ClockMajor,
} from "@shopify/polaris-icons"
import { useContext, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"
import CRXSettings from "../components/CRXSettings.jsx"
import Changelog from "../components/Changelog"
import { DiscordButton } from "../components/DiscordButton.jsx"
import { ExternalAffiliateButton } from "../components/ExternalAffiliateButton.jsx"
import { Feedback } from "../components/Feedback.jsx"
import { GlowingButtonContainer } from "../components/GlowingButtonContainer.jsx"
import { LanguageSelector } from "../components/LanguageSelector.jsx"
import Logo from "../components/Logo"
import Settings from "../components/Settings.jsx"
import { Survey } from "../components/Survey.jsx"
import { TopMenu } from "../components/TopMenu.jsx"
import Tutorial from "../components/Tutorial"
import { CreditsBalanceDisplay } from "../components/ai/CreditsBalanceDisplay.jsx"
import { KopyPlusCTA } from "../components/ai/KopyPlusCTA.jsx"
import { GlobalStateContext } from "../components/providers/GlobalStateProvider"
import { useDeviceType } from "../hooks/useDeviceType.js"
import { MCPInstructions } from "../components/MCPInstructions.jsx"
import {
	ScrollingSwapPromo,
	SCROLLING_SWAP_PROMO_ENABLED,
} from "../components/ScrollingSwapPromo/ScrollingSwapPromo.jsx"
import tryOurMcpArrow from "../assets/try_our_mcp_arrow.svg"

const HomePage = () => {
	const { state } = useContext(GlobalStateContext)
	const { loadingUserData, hasTutorialDismissed } = state
	const navigate = useNavigate()
	const { t } = useTranslation()
	const { isDesktop, isTablet } = useDeviceType()
	const [searchParams, setSearchParams] = useSearchParams()

	useEffect(() => {
		const importType = searchParams.get("importType")
		const importUrl = searchParams.get("importUrl")

		if (!importType || !importUrl) return

		if (importType === "product") {
			navigate("/single?importUrl=" + importUrl)
		} else if (importType === "create_product_page") {
			navigate("/pages?importUrl=" + importUrl)
		} else {
			navigate("/multiple?importUrl=" + importUrl)
		}
	}, [])

	return (
		<div>
			<TopMenu
				accountSelector
				storeSelector
				other={
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: "0.7rem",
							marginLeft: "auto",
						}}
					>
						{state.hasValidKopyPlusSubscription ? (
							<CreditsBalanceDisplay />
						) : (
							""
						)}

						{isDesktop ? <ExternalAffiliateButton /> : null}

						{(isTablet || isDesktop) && (
							<div style={{ position: "relative" }}>
								<MCPInstructions
									showTrigger={isTablet || isDesktop}
								/>
								<img
									src={tryOurMcpArrow}
									alt=""
									style={{
										position: "absolute",
										top: "calc(100% + 0.35rem)",
										left: "0.6rem",
										width: "8.5rem",
										pointerEvents: "none",
										userSelect: "none",
										transformOrigin: "top left",
									}}
								/>
							</div>
						)}

						{(isDesktop || isTablet) && <CRXSettings />}

						{(isDesktop || isTablet) && <DiscordButton />}

						<Tooltip
							content={
								<div style={{ padding: "0.2rem 0.4rem" }}>
									<span
										style={{
											fontSize: "0.8rem",
											color: "#222",
										}}
									>
										{t("header.history_tooltip")}
									</span>
								</div>
							}
						>
							<div
								style={{
									background: "#e7e7e7",
									borderRadius: "100%",
									padding: "0.4rem",
								}}
								onClick={() => navigate("/history")}
							>
								<div
									style={{
										cursor: "pointer",
										verticalAlign: "middle",
									}}
								>
									<Icon source={ClockMajor} color="subdued" />
								</div>
							</div>
						</Tooltip>

						<Changelog />

						<Settings />
					</div>
				}
				secondRow={
					isDesktop ? null : (
						<ExternalAffiliateButton isSecondRow={true} />
					)
				}
			/>

			<div
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					position: "relative",
					paddingBottom: "3rem",
				}}
			>
				{!loadingUserData && !hasTutorialDismissed && <Tutorial />}

				<div
					style={{
						marginTop: "3.48rem",
						marginBottom: "2rem",
					}}
				>
					<Logo />
				</div>

				{/* Do not remove this component, as its id is required for google tag manager */}
				<div
					id="home-items"
					style={{
						flexDirection: "column",
						display: "flex",
						marginBottom: "2rem",
						gap: "0.8rem",
						alignItems: "center",
					}}
				>
					{state.shop && state.accessToken ? (
						<>
							<div
								className="home-button"
								onClick={() => navigate("/single")}
							>
								<div style={{ width: "1.3rem" }}>
									<Icon
										source={AddProductMajor}
										color="base"
									/>
								</div>
								<p
									style={{
										fontSize: "1.25rem",
										color: "#454F5B",
									}}
								>
									{t("home.import_a_single_product")}
								</p>
							</div>

							<div
								className="home-button"
								onClick={() => navigate("/multiple")}
							>
								<div style={{ width: "1.3rem" }}>
									<Icon
										source={CategoriesMajor}
										color="base"
									/>
								</div>
								<p
									style={{
										fontSize: "1.25rem",
										color: "#454F5B",
									}}
								>
									{t("home.import_multiple_products")}
								</p>
							</div>

							{state.hasValidKopyPlusSubscription ? (
								<div>
									<GlowingButtonContainer intensity="medium">
										<div
											className="home-button"
											style={{
												borderColor: "#bbb",
												justifyContent: "center",
												paddingLeft: "0rem",
												gap: "0.3rem",
											}}
											onClick={() => navigate("/ai")}
										>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="1.5rem"
												height="1.5rem"
												viewBox="0 0 24 24"
											>
												<path
													fill="#2c2c2c"
													d="m10 19l-2.5-5.5L2 11l5.5-2.5L10 3l2.5 5.5L18 11l-5.5 2.5L10 19Zm8 2l-1.25-2.75L14 17l2.75-1.25L18 13l1.25 2.75L22 17l-2.75 1.25L18 21Z"
												></path>
											</svg>
											<p
												style={{
													fontSize: "1.25rem",
													fontWeight: "600",
													color: "#2c2c2c",
												}}
											>
												Kopy AI
											</p>
										</div>
									</GlowingButtonContainer>
									<div
										className="home-button"
										style={{
											marginTop: "0.8rem",
											justifyContent: "center",
											gap: "0.5rem",
										}}
										onClick={() => navigate("/pages")}
									>
										<svg
											style={{
												width: "1.3rem",
												height: "1.3rem",
												color: "#454F5B",
											}}
											viewBox="0 0 24 24"
										>
											<path
												fill="currentColor"
												d="M16.08 6.63c-.07-.1-.15-.19-.23-.27h-.01a2.85 2.85 0 0 0-1.11-.69l-1.38-.45c-.1-.04-.2-.11-.26-.2a.5.5 0 0 1-.1-.31a.5.5 0 0 1 .1-.31c.06-.09.15-.16.26-.2l1.38-.45c.42-.14.79-.38 1.1-.69c.29-.3.52-.67.67-1.07v-.03l.45-1.38c.04-.1.11-.2.2-.26a.5.5 0 0 1 .31-.1c.11 0 .22.04.31.1s.16.15.2.26l.45 1.38c.14.42.38.8.69 1.11c.31.32.69.55 1.11.69l1.38.45h.03c.1.04.19.11.26.2s.1.2.1.31a.5.5 0 0 1-.1.31c-.06.09-.15.16-.26.2l-1.38.45c-.42.14-.8.38-1.11.69c-.32.31-.55.69-.69 1.11l-.46 1.38v.03c-.04.09-.11.17-.19.23a.5.5 0 0 1-.31.1a.5.5 0 0 1-.31-.1a.52.52 0 0 1-.2-.26l-.45-1.38c-.1-.31-.25-.6-.45-.85m7.7 3.8l-.77-.24c-.24-.08-.45-.21-.62-.38c-.17-.18-.3-.39-.38-.62l-.25-.76a.33.33 0 0 0-.11-.15a.27.27 0 0 0-.34 0c-.05.04-.09.09-.11.15l-.25.76c-.07.23-.2.44-.37.61s-.38.3-.61.38l-.77.25c-.06.02-.11.06-.15.11a.27.27 0 0 0 0 .34c.04.05.09.09.15.11l.77.25c.23.07.45.21.62.38s.3.39.38.62l.25.76c.02.06.06.11.11.15a.27.27 0 0 0 .34 0c.05-.04.09-.09.11-.15l.25-.76c.08-.24.21-.45.38-.62c.18-.17.39-.3.62-.38l.77-.25c.06-.02.11-.06.15-.11s.05-.11.05-.17s-.01-.12-.05-.17s-.106-.08-.17-.11m-4.24 2.28l.26.8c.05.14.12.25.2.37v6.08c0 1.24-1.01 2.25-2.25 2.25H6.25C5.01 22.21 4 21.2 4 19.96V4.46c0-1.24 1.01-2.25 2.25-2.25h8.26c-.07.04-.15.08-.22.11l-1.42.46c-.4.15-.75.4-.99.75a2 2 0 0 0-.38 1.18c0 .43.13.84.37 1.18c.078.112.178.202.275.289l.045.041H7.75c-.41 0-.75.34-.75.75s.34.75.75.75h7.24l.035.058a1 1 0 0 1 .085.162l.46 1.41c.14.4.4.74.75.99c.35.24.76.37 1.18.37c0 .37.11.73.33 1.04s.52.53.91.67zm-11.79 5h8.5c.41 0 .75-.34.75-.75s-.34-.75-.75-.75h-8.5c-.41 0-.75.34-.75.75s.34.75.75.75m0-5h8.5c.41 0 .75-.34.75-.75s-.34-.75-.75-.75h-8.5c-.41 0-.75.34-.75.75s.34.75.75.75"
											></path>
										</svg>
										<p
											style={{
												fontSize: "1.25rem",
												fontWeight: "600",
												color: "#454F5B",
												lineHeight: "1",
												textAlign: "center",
											}}
										>
											{t("home.create_ai_product_page")}
										</p>
									</div>
								</div>
							) : (
								<KopyPlusCTA context="home" />
							)}

							<div
								className="home-button"
								style={{
									justifyContent: "center",
									gap: "0.5rem",
									background: "#222",
									position: "relative",
									marginBottom:
										!state.hasTrialedKopyProtectBefore
											? "1rem"
											: "0",
								}}
								onClick={() => navigate("/protect")}
							>
								<svg
									style={{
										height: "1.3rem",
										width: "1.3rem",
										color: "#fff",
									}}
									viewBox="0 0 512 512"
								>
									<path
										fill="currentColor"
										d="M256 0c4.6 0 9.2 1 13.4 2.9l188.3 79.9c22 9.3 38.4 31 38.3 57.2c-.5 99.2-41.3 280.7-213.6 363.2c-16.7 8-36.1 8-52.8 0C57.3 420.7 16.5 239.2 16 140c-.1-26.2 16.3-47.9 38.3-57.2L242.7 2.9C246.8 1 251.4 0 256 0m0 66.8v378C394 378 431.1 230.1 432 141.4z"
									></path>
								</svg>
								<p
									style={{
										fontSize: "1.25rem",
										fontWeight: "600",
										color: "#fff",
										lineHeight: "1",
										textAlign: "center",
									}}
								>
									{t("home.protect_from_competitors")}
								</p>

								{!state.hasTrialedKopyProtectBefore && (
									<div
										className="shadow"
										style={{
											position: "absolute",
											zIndex: 1,
											bottom: "-1.3rem",
											height: "1.3rem",
											color: "#eee",
											background: "#449d5e",
											padding: "0.3rem 0.6rem",
											borderRadius:
												"0rem 0rem 0.5rem 0.5rem",
											border: "1px solid #488a5c",
											borderTop: "none",
										}}
									>
										<div
											style={{
												display: "flex",
												gap: "0.2rem",
												alignItems: "center",
											}}
										>
											<svg
												width="0.65rem"
												height="0.65rem"
												viewBox="0 0 24 24"
											>
												<path
													fill="currentColor"
													d="M12.865 2.996a1 1 0 0 0-1.73 0L8.421 7.674a1.25 1.25 0 0 1-.894.608L2.44 9.05c-.854.13-1.154 1.208-.488 1.76l3.789 3.138c.35.291.515.75.43 1.197L5.18 20.35a1 1 0 0 0 1.448 1.072l4.79-2.522a1.25 1.25 0 0 1 1.164 0l4.79 2.522a1 1 0 0 0 1.448-1.072l-.991-5.205a1.25 1.25 0 0 1 .43-1.197l3.79-3.139c.665-.55.365-1.63-.49-1.759l-5.085-.768a1.25 1.25 0 0 1-.895-.608z"
												></path>
											</svg>
											<span
												style={{
													fontSize: "0.65rem",
													fontWeight: "700",
													lineHeight: "1",
													textTransform: "uppercase",
												}}
											>
												{t("protect.3_day_trial")}
											</span>
										</div>
									</div>
								)}
							</div>
							{state.hasValidKopyPlusSubscription ? (
								<div
									className="home-button"
									style={{
										position: "relative",
										isolation: "isolate",
										zIndex: 0,
									}}
									onClick={() => navigate("/themes")}
								>
									<div style={{ width: "1.4rem" }}>
										<Icon
											source={
												'<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(92, 95, 98, 1)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-gift-icon lucide-gift"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13"/><path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5"/></svg>'
											}
											color="base"
										/>
									</div>
									<p
										style={{
											textAlign: "center",
											fontSize: "1.25rem",
											color: "#454F5B",
										}}
									>
										{t(
											"home.claim_your_free_premium_theme_here",
										)}
									</p>
									<div
										className="shadow"
										style={{
											position: "absolute",
											bottom: "-1.3rem",
											height: "1.3rem",
											color: "#eee",
											background: "#449d5e",
											padding: "0.3rem 0.6rem",
											borderRadius:
												"0rem 0rem 0.5rem 0.5rem",
											border: "1px solid #488a5c",
											borderTop: "none",
										}}
									>
										<div
											style={{
												display: "flex",
												gap: "0.2rem",
												alignItems: "center",
											}}
										>
											<svg
												width="0.65rem"
												height="0.65rem"
												viewBox="0 0 24 24"
											>
												<path
													fill="currentColor"
													d="M12.865 2.996a1 1 0 0 0-1.73 0L8.421 7.674a1.25 1.25 0 0 1-.894.608L2.44 9.05c-.854.13-1.154 1.208-.488 1.76l3.789 3.138c.35.291.515.75.43 1.197L5.18 20.35a1 1 0 0 0 1.448 1.072l4.79-2.522a1.25 1.25 0 0 1 1.164 0l4.79 2.522a1 1 0 0 0 1.448-1.072l-.991-5.205a1.25 1.25 0 0 1 .43-1.197l3.79-3.139c.665-.55.365-1.63-.49-1.759l-5.085-.768a1.25 1.25 0 0 1-.895-.608z"
												></path>
											</svg>
											<span
												style={{
													fontSize: "0.65rem",
													fontWeight: "700",
													lineHeight: "1",
													textTransform: "uppercase",
												}}
											>
												{t("common.new")}
											</span>
										</div>
									</div>
								</div>
							) : null}
						</>
					) : (
						<div
							className="home-button"
							style={{
								padding: "1rem 3rem 1rem 1.5rem",
								gap: "1rem",
							}}
							onClick={() => navigate("/linkStore")}
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								style={{ color: "#454F5B" }}
								width="1.6rem"
								height="1.6rem"
								viewBox="0 0 24 24"
							>
								<path
									fill="currentColor"
									d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"
								></path>
							</svg>
							<p
								style={{
									fontSize: "1.25rem",
									color: "#454F5B",
								}}
							>
								{t("home.link_your_store")}
							</p>
						</div>
					)}
				</div>
			</div>

			<div
				style={{
					position: "fixed",
					top: "0",
					right: "0",
					bottom: "0",
					left: "0",
					zIndex: "500",
					display: "flex",
					justifyContent: "end",
					alignItems: "center",
					pointerEvents: "none",
				}}
			>
				<Feedback />
			</div>

			<div
				style={{
					position: "fixed",
					bottom: "2rem",
					left: "2rem",
					zIndex: "500",
				}}
			>
				<LanguageSelector />
			</div>

			{/* Fixed bottom left, above the language selector */}
			{SCROLLING_SWAP_PROMO_ENABLED &&
				!loadingUserData &&
				state.shop &&
				state.accessToken && <ScrollingSwapPromo />}

			<Survey />
		</div>
	)
}

export default HomePage
